import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { db } from './db';
import {
  Fish,
  FishLength,
  FishLimit,
  FishLimitMoreNote,
  FishLimitRow,
  FishType,
  FishesFishType,
  FishingTechnique,
  MoreNote,
  NoaaStation,
  SeedData,
  State,
} from '@core/models/reference-data.models';

/**
 * The SaltwaterUp API returns JSON where every field — including
 * primary keys, foreign keys, and numeric columns — is encoded as a
 * string (e.g. `{id: "103", fish_id: "41", sort_order: "5.00"}`).
 * This is an artifact of the original PHP/MySQL backend, which the
 * iOS client compensates for by going through SQLite column types
 * that implicitly coerce.
 *
 * In TypeScript / IndexedDB we have to do it explicitly. If we don't,
 * `db.fish_limits.where('state_id').equals(5)` returns zero matches
 * because the indexed values are all strings like "5". We normalize
 * on the way in so every record in Dexie has proper number types,
 * matching both our TypeScript interfaces and the bundled seed JSON.
 */
function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '';
  return String(v);
}

function normState(r: unknown): State {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    sort_order: toNum(o['sort_order']),
  };
}

function normFishType(r: unknown): FishType {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    sort_order: toNum(o['sort_order']),
  };
}

function normFish(r: unknown): Fish {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    image_name: toStr(o['image_name']),
    sort_order: toNum(o['sort_order']),
  };
}

function normFishesFishType(r: unknown): FishesFishType {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    fish_id: toNum(o['fish_id']),
    fish_type_id: toNum(o['fish_type_id']),
  };
}

function normFishingTechnique(r: unknown): FishingTechnique {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    sort_order: toNum(o['sort_order']),
  };
}

function normFishLength(r: unknown): FishLength {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    length: toNum(o['length']),
  };
}

function normFishLimit(r: unknown): FishLimit {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    fish_id: toNum(o['fish_id']),
    state_id: toNum(o['state_id']),
    daily_bag: toStr(o['daily_bag']),
    min_length: toStr(o['min_length']),
    max_length: toStr(o['max_length']),
    notes: toStr(o['notes']),
    start_date: toStr(o['start_date']),
    end_date: toStr(o['end_date']),
  };
}

function normFishLimitMoreNote(r: unknown): FishLimitMoreNote {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    fish_limit_id: toNum(o['fish_limit_id']),
    more_note_id: toNum(o['more_note_id']),
  };
}

function normMoreNote(r: unknown): MoreNote {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    notes: toStr(o['notes']),
    sort_order: toNum(o['sort_order']),
  };
}

function normNoaaStation(r: unknown): NoaaStation {
  const o = r as Record<string, unknown>;
  return {
    id: toNum(o['id']),
    name: toStr(o['name']),
    lat: toNum(o['lat']),
    lon: toNum(o['lon']),
    noaa_station_number: toNum(o['noaa_station_number']),
    is_active: toNum(o['is_active']),
  };
}

@Injectable({ providedIn: 'root' })
export class ReferenceDataService {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);

  /**
   * Seeds the local DB from the bundled seed-data.json if the DB has
   * never been populated. Safe to call on every app startup — a no-op
   * after the first successful run.
   */
  async ensureInitialized(): Promise<void> {
    const meta = await db.meta.get(0);
    if (meta && meta.data_version > 0) return;

    const seed = await firstValueFrom(
      this.http.get<SeedData>('/assets/seed-data.json'),
    );

    // Seed data comes from SQLite via our Python export, so types are
    // already correct. We still pass each row through a normalizer to
    // keep both write paths identical — this way ensureInitialized() +
    // syncIfStale() produce Dexie rows of exactly the same shape.
    await db.transaction(
      'rw',
      [
        db.states,
        db.fish_types,
        db.fishes,
        db.fishes_fish_types,
        db.fishing_techniques,
        db.fish_lengths,
        db.fish_limits,
        db.fish_limits_more_notes,
        db.more_notes,
        db.noaa_stations,
        db.meta,
      ],
      async () => {
        await db.states.bulkPut(seed.tables.states.map(normState));
        await db.fish_types.bulkPut(seed.tables.fish_types.map(normFishType));
        await db.fishes.bulkPut(seed.tables.fishes.map(normFish));
        await db.fishes_fish_types.bulkPut(
          seed.tables.fishes_fish_types.map(normFishesFishType),
        );
        await db.fishing_techniques.bulkPut(
          seed.tables.fishing_techniques.map(normFishingTechnique),
        );
        await db.fish_lengths.bulkPut(
          seed.tables.fish_lengths.map(normFishLength),
        );
        await db.fish_limits.bulkPut(seed.tables.fish_limits.map(normFishLimit));
        await db.fish_limits_more_notes.bulkPut(
          seed.tables.fish_limits_more_notes.map(normFishLimitMoreNote),
        );
        await db.more_notes.bulkPut(seed.tables.more_notes.map(normMoreNote));
        await db.noaa_stations.bulkPut(
          seed.tables.noaa_stations.map(normNoaaStation),
        );

        await db.meta.put({
          id: 0,
          data_version: toNum(seed.data_version),
          schema_version: toNum(seed.schema_version),
          last_synced_at: null,
        });
      },
    );
  }

  /**
   * Checks /configs.json for a newer data_version than what we have
   * cached and, if so, pulls fresh copies of all ten reference tables.
   * Network failures are swallowed (non-fatal) — the local cache
   * remains valid for offline use.
   */
  async syncIfStale(): Promise<boolean> {
    try {
      const configs = await firstValueFrom(this.api.getConfigs());
      if (configs.length === 0) return false;

      const remote = configs[0];
      if (remote === undefined) return false;
      const remoteDataVersion = toNum(remote.data_version);
      const remoteSchemaVersion = toNum(remote.schema_version);

      const meta = await db.meta.get(0);
      if (meta && remoteDataVersion <= meta.data_version) {
        return false;
      }

      const [
        states,
        fishTypes,
        fishes,
        fishesFishTypes,
        fishingTechniques,
        fishLengths,
        fishLimits,
        fishLimitsMoreNotes,
        moreNotes,
        noaaStations,
      ] = await Promise.all([
        firstValueFrom(this.api.getStates()),
        firstValueFrom(this.api.getFishTypes()),
        firstValueFrom(this.api.getFishes()),
        firstValueFrom(this.api.getFishesFishTypes()),
        firstValueFrom(this.api.getFishingTechniques()),
        firstValueFrom(this.api.getFishLengths()),
        firstValueFrom(this.api.getFishLimits()),
        firstValueFrom(this.api.getFishLimitsMoreNotes()),
        firstValueFrom(this.api.getMoreNotes()),
        firstValueFrom(this.api.getNoaaStations()),
      ]);

      await db.transaction(
        'rw',
        [
          db.states,
          db.fish_types,
          db.fishes,
          db.fishes_fish_types,
          db.fishing_techniques,
          db.fish_lengths,
          db.fish_limits,
          db.fish_limits_more_notes,
          db.more_notes,
          db.noaa_stations,
          db.meta,
        ],
        async () => {
          await db.states.clear();
          await db.states.bulkPut(states.map(normState));
          await db.fish_types.clear();
          await db.fish_types.bulkPut(fishTypes.map(normFishType));
          await db.fishes.clear();
          await db.fishes.bulkPut(fishes.map(normFish));
          await db.fishes_fish_types.clear();
          await db.fishes_fish_types.bulkPut(
            fishesFishTypes.map(normFishesFishType),
          );
          await db.fishing_techniques.clear();
          await db.fishing_techniques.bulkPut(
            fishingTechniques.map(normFishingTechnique),
          );
          await db.fish_lengths.clear();
          await db.fish_lengths.bulkPut(fishLengths.map(normFishLength));
          await db.fish_limits.clear();
          await db.fish_limits.bulkPut(fishLimits.map(normFishLimit));
          await db.fish_limits_more_notes.clear();
          await db.fish_limits_more_notes.bulkPut(
            fishLimitsMoreNotes.map(normFishLimitMoreNote),
          );
          await db.more_notes.clear();
          await db.more_notes.bulkPut(moreNotes.map(normMoreNote));
          await db.noaa_stations.clear();
          await db.noaa_stations.bulkPut(noaaStations.map(normNoaaStation));

          await db.meta.put({
            id: 0,
            data_version: remoteDataVersion,
            schema_version: remoteSchemaVersion,
            last_synced_at: new Date().toISOString(),
          });
        },
      );

      return true;
    } catch (err) {
      console.warn('ReferenceDataService.syncIfStale failed', err);
      return false;
    }
  }

  /**
   * Returns the Fish Limits grid rows for a given state and fish type,
   * filtering to FishLimits that are active for today. Port of
   * FishLimitVM.getSqlQueryGivenFishType:andState: (plus, when the
   * fish_type is "All", FishLimitVM.getSqlQueryForAll).
   */
  async getFishLimitRows(
    stateId: number,
    fishTypeId: number,
  ): Promise<FishLimitRow[]> {
    const today = this.today();

    // "All" is a special pseudo-fish-type in the iOS data (fish_types.id=11,
    // name='All'). It doesn't have corresponding fishes_fish_types join
    // rows — the iOS client switches to FishLimitVM.getSqlQueryForAll
    // which returns every fish with an active regulation regardless of
    // category. We mirror that branch here. Any other fish_type_id goes
    // through the normal joined query path.
    const isAll = await this.isAllFishType(fishTypeId);

    const [fishesForType, limitsForState, moreNotesById, joinRows] =
      await Promise.all([
        // Filter by fish_type_id using the single-field index. We previously
        // used the compound [fish_id+fish_type_id] index with a `between`
        // range but Dexie compound ranges don't do "any a, specific b" —
        // they match by lexicographic ordering of the concatenated key, so
        // our open-ended fish_id range effectively matched every row.
        isAll
          ? Promise.resolve([])
          : db.fishes_fish_types
              .where('fish_type_id')
              .equals(fishTypeId)
              .toArray(),
        db.fish_limits
          .where('state_id')
          .equals(stateId)
          .filter((l) => l.start_date <= today && today <= l.end_date)
          .toArray(),
        db.more_notes.toArray().then((rows) => {
          const map = new Map<number, string>();
          for (const r of rows) map.set(r.id, r.notes);
          return map;
        }),
        db.fish_limits_more_notes.toArray(),
      ]);

    const fishIdsInType = new Set(fishesForType.map((r) => r.fish_id));

    const moreNoteIdsByLimitId = new Map<number, number[]>();
    for (const row of joinRows) {
      const arr = moreNoteIdsByLimitId.get(row.fish_limit_id);
      if (arr) arr.push(row.more_note_id);
      else moreNoteIdsByLimitId.set(row.fish_limit_id, [row.more_note_id]);
    }

    // In "All" mode we don't filter by fish_type — every active
    // fish_limit for the chosen state is relevant.
    const relevantLimits = isAll
      ? limitsForState
      : limitsForState.filter((l) => fishIdsInType.has(l.fish_id));

    const fishIds = Array.from(new Set(relevantLimits.map((l) => l.fish_id)));
    const fishMap = new Map<
      number,
      { name: string; image_name: string; sort_order: number }
    >();
    for (const f of await db.fishes.where('id').anyOf(fishIds).toArray()) {
      fishMap.set(f.id, {
        name: f.name,
        image_name: f.image_name,
        sort_order: f.sort_order,
      });
    }

    const rows: FishLimitRow[] = relevantLimits.flatMap((limit) => {
      const fish = fishMap.get(limit.fish_id);
      if (!fish) return [];

      const moreNoteIds = moreNoteIdsByLimitId.get(limit.id) ?? [];
      const moreNotesHtml = moreNoteIds
        .map((id) => moreNotesById.get(id))
        .filter((s): s is string => typeof s === 'string' && s.length > 0)
        .join('<br /><br />');

      return [
        {
          fish_id: limit.fish_id,
          fish_name: fish.name,
          image_name: fish.image_name,
          daily_bag: limit.daily_bag,
          min_length: limit.min_length,
          max_length: limit.max_length,
          notes: limit.notes,
          more_notes_html: moreNotesHtml,
          has_special_regs: moreNotesHtml.length > 0,
        },
      ];
    });

    rows.sort((a, b) => {
      const sa = fishMap.get(a.fish_id)?.sort_order ?? 0;
      const sb = fishMap.get(b.fish_id)?.sort_order ?? 0;
      return sa - sb;
    });

    return rows;
  }

  private today(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /**
   * Whether a given fish_type_id refers to the special "All" pseudo-type.
   * We look this up by name rather than assuming a hard-coded id so a
   * future server-side re-seed can't accidentally renumber things.
   * Cached after first read since fish_types rarely changes.
   */
  private cachedAllFishTypeId: number | null | undefined = undefined;
  private async isAllFishType(fishTypeId: number): Promise<boolean> {
    if (this.cachedAllFishTypeId === undefined) {
      const all = await db.fish_types.filter((t) => t.name === 'All').first();
      this.cachedAllFishTypeId = all?.id ?? null;
    }
    return this.cachedAllFishTypeId !== null && fishTypeId === this.cachedAllFishTypeId;
  }

  /**
   * Proactively fetches every fish image so it lands in the service
   * worker's fish-images cache group. Intended for the "Download for
   * offline use" flow — a user on wifi tapping a button before heading
   * out on a trip where they expect to lose cell signal.
   *
   * Implementation is just a parallel batch of `fetch()` calls; we
   * don't need the responses for anything. The service worker intercepts
   * each one and writes it to the cache. We cap concurrency at 6 so a
   * burst of 99 parallel requests doesn't trigger connection-limit
   * throttling by the browser.
   *
   * Progress is reported via the supplied callback so the UI can show a
   * progress bar. Failures on individual images are swallowed — we want
   * to cache as many as we can rather than aborting on the first 404.
   *
   * Returns the number of images successfully fetched.
   */
  async warmUpFishImages(
    onProgress?: (done: number, total: number) => void,
    imageUrlBase?: string,
  ): Promise<number> {
    const fishes = await db.fishes.toArray();
    const total = fishes.length;
    if (total === 0) return 0;

    const base =
      imageUrlBase ?? 'https://www.saltwaterup.com/img/fish/';
    const concurrency = 6;
    let done = 0;
    let succeeded = 0;

    onProgress?.(0, total);

    // Simple bounded-parallelism queue. Workers pull indexes off a
    // shared counter until the queue drains.
    let nextIndex = 0;
    const runWorker = async (): Promise<void> => {
      while (nextIndex < total) {
        const idx = nextIndex++;
        const fish = fishes[idx];
        if (!fish) continue;
        const url = `${base}${fish.image_name}`;
        try {
          const response = await fetch(url, { cache: 'no-store' });
          if (response.ok) succeeded++;
        } catch {
          // Network error / offline — swallow. User gets whatever we
          // could cache and can retry later.
        }
        done++;
        onProgress?.(done, total);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(concurrency, total) }, () => runWorker()),
    );

    return succeeded;
  }
}
